import { fromCents, toCents } from "../utils/currencyUtils";

export const PLATFORM_FEE_RATE = 0.02;
export const CARD_PROCESSING_PERCENT = 0.029;
export const CARD_PROCESSING_FIXED_CENTS = 30;

const emptyQuote = () => ({
  signupIds: [],
  advertisedCents: 0,
  deskDueCents: 0,
  platformFeeCents: 0,
  processingFeeCents: 0,
  onlineFeeCents: 0,
  amountCents: 0,
  applicationFeeCents: 0,
  currency: "usd",
  status: "open",
  items: [],
  lines: [],
});

export const isDueCartLine = (line) => {
  if (!line) return false;
  if (line.waitlisted || line.status === "waitlisted") return false;
  if (line.status === "cancelled" || line.status === "denied") return false;
  const status = line.paymentStatus || "unpaid";
  return status === "unpaid" || status === "held";
};

export const lineAmountsCents = (line) => {
  const bowlerCount = Math.max(
    1,
    1 + (Array.isArray(line?.teammates) ? line.teammates.length : 0)
  );
  const entryCents =
    line?.entryCents != null
      ? Number(line.entryCents)
      : toCents(Number(line?.event?.entryFee || 0) * bowlerCount);
  const sidepotCents =
    line?.sidepotCents != null
      ? Number(line.sidepotCents)
      : toCents(Number(line?.sidepotTotal || 0));
  const bracketCents =
    line?.bracketCents != null
      ? Number(line.bracketCents)
      : toCents(Number(line?.bracketTotal || 0));
  const advertisedCents =
    line?.advertisedCents != null
      ? Number(line.advertisedCents)
      : entryCents + sidepotCents + bracketCents;
  return {
    signupId: line?.id || null,
    label: line?.event?.eventName || "Entry",
    entryCents,
    sidepotCents,
    bracketCents,
    advertisedCents,
  };
};

export const platformFeeCents = (advertisedCents) => {
  const amount = Number(advertisedCents) || 0;
  if (amount <= 0) return 0;
  return Math.round(amount * PLATFORM_FEE_RATE);
};

export const stripeFeeCents = (chargeCents) => {
  const amount = Number(chargeCents) || 0;
  if (amount <= 0) return 0;
  return Math.round(amount * CARD_PROCESSING_PERCENT) + CARD_PROCESSING_FIXED_CENTS;
};

const orgNetCents = (chargeCents, advertisedCents) =>
  chargeCents - stripeFeeCents(chargeCents) - platformFeeCents(advertisedCents);

export const cardChargeCents = (advertisedCents) => {
  const advertised = Number(advertisedCents) || 0;
  if (advertised <= 0) return 0;
  const platform = platformFeeCents(advertised);
  const netWanted = advertised + platform;
  let charge = Math.round(
    (netWanted + CARD_PROCESSING_FIXED_CENTS) / (1 - CARD_PROCESSING_PERCENT)
  );
  while (orgNetCents(charge, advertised) < advertised) {
    charge += 1;
  }
  while (charge > 0 && orgNetCents(charge - 1, advertised) >= advertised) {
    charge -= 1;
  }
  return charge;
};

const pushItem = (items, signupId, kind, amountCents, label) => {
  if (!amountCents) return;
  items.push({
    signupID: signupId,
    kind,
    amountCents,
    label,
  });
};

export const quoteSignupPayment = (
  lines = [],
  { organizationId = null, tournamentId = null, userId = null, email = null } = {}
) => {
  const dueLines = (lines || []).filter(isDueCartLine);
  if (!dueLines.length) return emptyQuote();

  const items = [];
  const breakdown = [];
  let advertisedCents = 0;

  dueLines.forEach((line) => {
    const amounts = lineAmountsCents(line);
    advertisedCents += amounts.advertisedCents;
    breakdown.push(amounts);
    pushItem(items, amounts.signupId, "event", amounts.entryCents, amounts.label);
    pushItem(
      items,
      amounts.signupId,
      "sidepot",
      amounts.sidepotCents,
      "Sidepots"
    );
    pushItem(
      items,
      amounts.signupId,
      "bracket",
      amounts.bracketCents,
      "Brackets"
    );
  });

  const platform = platformFeeCents(advertisedCents);
  const charge = cardChargeCents(advertisedCents);
  const processing = charge - advertisedCents - platform;

  if (platform > 0) {
    pushItem(items, null, "platform_fee", platform, "Platform fee");
  }
  if (processing > 0) {
    pushItem(items, null, "processing_fee", processing, "Card processing");
  }

  return {
    organizationID: organizationId,
    tournamentID: tournamentId,
    userID: userId,
    email,
    signupIds: dueLines.map((line) => line.id).filter(Boolean),
    advertisedCents,
    deskDueCents: advertisedCents,
    platformFeeCents: platform,
    processingFeeCents: processing,
    onlineFeeCents: platform + processing,
    amountCents: charge,
    applicationFeeCents: platform,
    currency: "usd",
    status: "open",
    items,
    lines: breakdown,
  };
};

const ADVERTISED_ITEM_KINDS = new Set(["event", "sidepot", "bracket"]);

const itemCents = (items, match) =>
  (items || []).reduce((sum, item) => {
    if (!match(item)) return sum;
    return sum + Number(item.amountCents || 0);
  }, 0);

/** Advertised amounts plus the 2% platform fee. Card processing is not refunded. */
export const payNowRefundShare = (items = [], signupId) => {
  const advertised = itemCents(items, (item) =>
    ADVERTISED_ITEM_KINDS.has(item.kind)
  );
  const thisAdvertised = itemCents(
    items,
    (item) =>
      ADVERTISED_ITEM_KINDS.has(item.kind) && item.signupID === signupId
  );
  const platform = itemCents(items, (item) => item.kind === "platform_fee");
  const platformFeeCents =
    advertised > 0 && thisAdvertised > 0
      ? Math.round(platform * (thisAdvertised / advertised))
      : 0;
  return {
    advertisedCents: thisAdvertised,
    platformFeeCents,
    amountCents: thisAdvertised + platformFeeCents,
  };
};

export const payNowRefundCents = (items = [], signupId) =>
  payNowRefundShare(items, signupId).amountCents;

export const advertisedSignupIdsFromItems = (items = []) => [
  ...new Set(
    (items || [])
      .filter(
        (item) =>
          item?.signupID && ADVERTISED_ITEM_KINDS.has(String(item.kind || ""))
      )
      .map((item) => item.signupID)
  ),
];

export const refundAmountForSignupOnCharge = (
  items = [],
  signupId,
  paymentAmountCents
) => {
  const share = payNowRefundShare(items, signupId);
  if (share.amountCents <= 0) {
    return { ok: false, amountCents: 0, share };
  }
  const cap = Number(paymentAmountCents) || 0;
  return {
    ok: true,
    amountCents: cap > 0 ? Math.min(share.amountCents, cap) : share.amountCents,
    share,
  };
};

/** One card refund for every line of a required group on this charge. */
export const refundAmountForSignupsOnCharge = (
  items = [],
  signupIds = [],
  paymentAmountCents
) => {
  const shares = [...new Set((signupIds || []).filter(Boolean))].map(
    (signupId) => ({
      signupId,
      ...payNowRefundShare(items, signupId),
    })
  );
  const advertisedCents = shares.reduce(
    (sum, share) => sum + share.advertisedCents,
    0
  );
  const platformFeeCents = shares.reduce(
    (sum, share) => sum + share.platformFeeCents,
    0
  );
  const amountCents = advertisedCents + platformFeeCents;
  const share = { advertisedCents, platformFeeCents, amountCents, shares };
  if (amountCents <= 0) {
    return { ok: false, amountCents: 0, share };
  }
  const cap = Number(paymentAmountCents) || 0;
  return {
    ok: true,
    amountCents: cap > 0 ? Math.min(amountCents, cap) : amountCents,
    share,
  };
};

export const allAdvertisedSignupsRefunded = (
  items = [],
  refundedSignupIds = []
) => {
  const onCharge = advertisedSignupIdsFromItems(items);
  if (!onCharge.length) return false;
  const refunded = new Set((refundedSignupIds || []).filter(Boolean));
  return onCharge.every((id) => refunded.has(id));
};

export const attributedSignupIdsFromCharge = (payload = null) => {
  const fromRefunds = (payload?.refunds?.data || [])
    .map((refund) => refund?.metadata?.signup_id)
    .filter(Boolean);
  const fromCharge = payload?.metadata?.signup_id;
  return [...new Set([...fromRefunds, fromCharge].filter(Boolean))];
};

export const signupIdsToMarkRefunded = ({
  attributedSignupIds = [],
  signupIdsOnPayment = [],
  fullRefund = false,
} = {}) => {
  const attributed = [...new Set((attributedSignupIds || []).filter(Boolean))];
  if (attributed.length) return attributed;
  if (fullRefund) {
    return [...new Set((signupIdsOnPayment || []).filter(Boolean))];
  }
  return [];
};

export const quoteDollars = (cents) => fromCents(Number(cents) || 0);

export const paymentInsertFromQuote = (quote) => ({
  organizationID: quote.organizationID,
  tournamentID: quote.tournamentID,
  userID: quote.userID,
  email: quote.email,
  amountCents: quote.amountCents,
  applicationFeeCents: quote.applicationFeeCents,
  currency: quote.currency || "usd",
  status: quote.status || "open",
  items: (quote.items || []).map((item) => ({
    signupID: item.signupID,
    kind: item.kind,
    amountCents: item.amountCents,
  })),
});
